if (!customElements.get("cart-complementary-products")) {
  customElements.define(
    "cart-complementary-products",
    class CartComplementaryProducts extends HTMLElement {
      static storageKey = "cart-complementary-recent-variants";
      static recentVariantIds = (() => {
        try {
          const stored = JSON.parse(sessionStorage.getItem("cart-complementary-recent-variants") || "[]");
          return Array.isArray(stored) ? stored.map(String) : [];
        } catch {
          return [];
        }
      })();

      static rememberAddedItems(items) {
        const addedIds = items
          .map((item) => String(item.variant_id || item.id || ""))
          .filter(Boolean);
        this.recentVariantIds = [...new Set([...addedIds, ...this.recentVariantIds])].slice(0, 100);
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(this.recentVariantIds));
        } catch {}
      }

      connectedCallback() {
        this.load();
      }

      async load() {
        const container = this.querySelector("[data-swiper-container]");
        const data = this.querySelector("[data-cart-complementary-products-json]");

        if (!data || !container) return;

        try {
          const { sources = [] } = JSON.parse(data.textContent);
          const currentIds = new Set(sources.map((item) => String(item.variant_id)));
          this.constructor.recentVariantIds = this.constructor.recentVariantIds.filter(
            (id) => currentIds.has(id),
          );
          const knownIds = new Set(this.constructor.recentVariantIds);
          this.constructor.rememberAddedItems(
            sources.filter((item) => !knownIds.has(String(item.variant_id))),
          );
          const recentIds = this.constructor.recentVariantIds;
          let source;
          for (const variantId of recentIds) {
            source = sources.find((item) => String(item.variant_id) === variantId);
            if (source) break;
          }
          source ||= sources[0];
          const products = (source?.products || []).filter((product) =>
            product.variants?.some((variant) => variant.available),
          );
          this.dataset.productId = source ? String(source.product_id) : "";

          if (!products.length) {
            this.hidden = true;
            this.remove();
            return;
          }

          container.replaceChildren(
            ...products.map((product) => this.createSlide(product)),
          );
          this.hidden = false;
          await this.updateSlider();
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Unable to load complementary cart products", error);
          }
          this.hidden = true;
        }
      }

      createSlide(product) {
        const slide = document.createElement("div");
        const variant =
          product.variants?.find((item) => item.available) ||
          product.variants?.[0];
        const productUrl = product.url || `/products/${product.handle}`;
        const imageUrl =
          typeof product.featured_image === "string"
            ? product.featured_image
            : product.featured_image?.url || product.images?.[0] || "";
        const available = Boolean(variant?.available);
        const formId = `CartDrawerComplementary-${product.id}`;
        const addLabel = this.dataset.addLabel || "Add to cart";
        const soldOutLabel = this.dataset.soldOutLabel || "Sold out";
        const buttonLabel = available ? addLabel : soldOutLabel;
        const image = imageUrl
          ? `<a href="${this.escape(productUrl)}" aria-label="${this.escape(product.title)}">
              <picture class="lux-complementary__card-picture">
                <img
                  src="${this.escape(imageUrl)}"
                  alt="${this.escape(product.featured_image?.alt || product.title)}"
                  class="lux-complementary__card-image"
                  width="400"
                  height="400"
                  loading="lazy"
                >
              </picture>
            </a>`
          : "";

        slide.className =
          "lux-complementary__slide swiper-slide wt-slider__slide";
        slide.setAttribute("data-swiper-slide", "");
        slide.innerHTML = `
          <article class="lux-complementary__card">
            ${image}
            <div class="lux-complementary__card-body">
              <h3 class="lux-complementary__card-title">
                <a href="${this.escape(productUrl)}">${this.escape(product.title)}</a>
              </h3>
              <div class="lux-complementary__card-price">${product.price_html}</div>
              <product-form class="lux-complementary__card-form-wrapper">
                <form
                  method="post"
                  action="${this.escape(this.dataset.cartAddUrl || "/cart/add")}" 
                  id="${this.escape(formId)}"
                  class="wt-product__add-to-cart_form form lux-complementary__card-form"
                  data-type="add-to-cart-form"
                >
                  <div class="wt-product__add-to-cart_form--wrapper">
                    <input type="hidden" name="id" value="${this.escape(variant?.id || "")}">
                    <button
                      aria-label="${this.escape(buttonLabel)}"
                      id="ProductSubmitButton-${this.escape(formId)}"
                      type="submit"
                      name="add"
                      data-is-product-available="${available}"
                      class="product-form__submit lux-complementary__card-submit"
                      ${available ? "" : "disabled"}
                    >
                      <span class="product-button-pricing__wrapper">
                        <span class="product-button-pricing">
                          <span class="product-button-pricing__label">${this.escape(buttonLabel)}</span>
                        </span>
                      </span>
                      <span class="loading-overlay__spinner hidden">
                        <span class="lds-ring" aria-hidden="true">
                          <span></span><span></span><span></span><span></span>
                        </span>
                      </span>
                    </button>
                  </div>
                </form>
                <div class="product-form__error-message-wrapper">
                  <p class="product-form__error-message"></p>
                </div>
              </product-form>
            </div>
          </article>`;

        return slide;
      }

      async updateSlider() {
        await customElements.whenDefined("slideshow-section");
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const slider = this.querySelector("slideshow-section");
        slider?.swiper?.updateSize();
        slider?.swiper?.updateSlides();
        slider?.swiper?.update();
      }

      escape(value) {
        return String(value ?? "")
          .replaceAll("&", "&amp;")
          .replaceAll('"', "&quot;")
          .replaceAll("'", "&#039;")
          .replaceAll("<", "&lt;")
          .replaceAll(">", "&gt;");
      }
    },
  );

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.productVariantId && event.cartData) {
      customElements.get("cart-complementary-products").rememberAddedItems([
        { variant_id: event.productVariantId },
      ]);
    }
  });
}
